import { useEffect, useRef } from "react";
import { useReportBuilderStore } from "../../../hooks/reportBuilder.hooks";
import { ReportDashboard } from "../../../models/ReportBuilder";
import { useSaveDashboard, useStoredDashboards } from "../../../networking/report-dashboards.hooks";

/** A local edit is pushed once the user pauses, not on every keystroke or drag. */
const PUSH_DEBOUNCE_MS = 1500;

const own = (dashboards: ReportDashboard[]) => dashboards.filter((d) => !d.owner);

/**
 * Ties the user's dashboards to the server. Mounted once, next to the menu
 * that lists them, so a dashboard is stored whichever page it was created on.
 *
 * On the first pull the server's copies win for anything stored on both — every
 * change is pushed within a second or two, so the stored copy is the newer one.
 * A dashboard the server does not have is pushed up only if this browser has
 * never stored it (one made while the app was offline, or before dashboards
 * moved to the server); one it *has* stored is gone because its owner deleted
 * it, here or in another browser, and `adoptServerDashboards` drops it rather
 * than re-creating it. After that, any local change is pushed on a debounce.
 * Dashboards someone else owns are only in view: never pushed, and never
 * written back over the owner's copy (the backend refuses that too).
 */
export function useDashboardSync(enabled: boolean) {
	const store = useReportBuilderStore();
	const { data } = useStoredDashboards(enabled);
	const save = useSaveDashboard();
	const merged = useRef(false);
	/** Per dashboard: the JSON last handed to the server, to spot real changes. */
	const lastSent = useRef(new Map<string, string>());
	const timer = useRef<number>();

	/** Stores one dashboard and remembers that the server now has it. */
	const push = (dashboard: ReportDashboard) =>
		save.mutate(
			{ dashboard, visibility: dashboard.visibility ?? "private" },
			{ onSuccess: () => useReportBuilderStore.getState().markSynced(dashboard.id) }
		);

	useEffect(() => {
		if (!data || merged.current) return;

		const stored = new Set(data.own.map((d) => d.id));
		const never = new Set(store.syncedIds);
		const onlyHere = own(store.dashboards).filter((d) => !stored.has(d.id) && !never.has(d.id));

		store.adoptServerDashboards(data.own);
		store.setSharedDashboards(data.shared);
		own(useReportBuilderStore.getState().dashboards).forEach((d) =>
			lastSent.current.set(d.id, JSON.stringify(d))
		);
		merged.current = true;

		onlyHere.forEach((dashboard) => {
			lastSent.current.set(dashboard.id, JSON.stringify(dashboard));
			push(dashboard);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	// A later pull only refreshes what colleagues share; own dashboards are this session's.
	useEffect(() => {
		if (data && merged.current) store.setSharedDashboards(data.shared);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.shared]);

	const serialized = JSON.stringify(own(store.dashboards).map((d) => [d.id, d]));

	useEffect(() => {
		if (!enabled || !merged.current) return;
		window.clearTimeout(timer.current);
		timer.current = window.setTimeout(() => {
			own(useReportBuilderStore.getState().dashboards).forEach((dashboard) => {
				const json = JSON.stringify(dashboard);
				if (lastSent.current.get(dashboard.id) === json) return;
				lastSent.current.set(dashboard.id, json);
				push(dashboard);
			});
		}, PUSH_DEBOUNCE_MS);
		return () => window.clearTimeout(timer.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, serialized]);
}
